// Lightweight PCM (Int16, mono, 24000 Hz) streaming player using Web Audio API.
// Converts incoming base64 chunks to Float32 and resamples to the AudioContext sampleRate.
// Integrates with audioService volume/mute.
// ("pcm" means Pulse Code Modulation)
import { audioService } from "src/services/audioService";
import { STREAMING_AUDIO_SAMPLE_RATE, DEFAULT_AUDIO_CHANNELS } from "src/constants/audioConfig";
import { isCapacitorNative, isVegaLipSyncEnabled, VegaLipSyncCharacter, VegaLipSyncSession } from "src/lipsync/vegaLipSync";

type PcmChunkPlayerOptions = {
  sourceSampleRate?: number; // defaults to STREAMING_AUDIO_SAMPLE_RATE
  channels?: number; // defaults to DEFAULT_AUDIO_CHANNELS (mono)
  label?: string; // debug label
  playbackRate?: number; // 1.0 = normal speed
  /** Called once when playback actually starts (after buffer has enough samples). Use to sync character lipsync with audio. */
  onPlaybackStarted?: () => void;
  /** Called when playback ends (queue is empty and no more chunks for a period). Use to stop character talking animation. */
  onPlaybackEnded?: () => void;
  /** When Vega lip sync is on, analyze source-rate PCM as it is enqueued (lookahead). */
  vegaCharacterRef?: { current: VegaLipSyncCharacter | null };
};

export class PcmChunkPlayer {
  private ctx: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private queue: Float32Array[] = [];
  private isStarted = false;
  private stopped = false;
  private isPlaying = false; // Track if we've started playing (after minimum buffer)
  private isPaused = false; // Track if playback is paused (for ducking when user speaks)
  private savedGainValue = 1.0; // Save gain value before pausing
  private minBufferSamples: number; // Minimum samples needed before starting playback (~0.2s)
  private readonly sourceSampleRate: number;
  private readonly channels: number;
  private readonly label?: string;
  private readonly playbackRate: number;
  private unsubscribeMute?: () => void;
  private unsubscribeVolume?: () => void;
  private onPlaybackStarted?: () => void;
  private playbackStartedFired = false;
  private onPlaybackEnded?: () => void;
  private queueEmptyStartTime: number | null = null;
  private readonly QUEUE_EMPTY_THRESHOLD_MS = 500; // Consider playback ended if queue is empty for 500ms
  /** When false, empty queue is treated as a streaming gap (more chunks may arrive). Call when no more PCM will be added. */
  private endOfPcmStreamSignaled = false;
  private vegaSession: VegaLipSyncSession | null = null;
  private vegaCharacterRef?: { current: VegaLipSyncCharacter | null };
  private playedOutputSamples = 0;
  // Capacitor trade only: a looping silent <audio> keeps the ambient session pulling WebAudio.
  private sessionAudio: HTMLAudioElement | null = null;
  private sessionAudioUrl: string | null = null;

  constructor(options?: PcmChunkPlayerOptions) {
    this.sourceSampleRate = options?.sourceSampleRate ?? STREAMING_AUDIO_SAMPLE_RATE;
    this.channels = options?.channels ?? DEFAULT_AUDIO_CHANNELS;
    this.label = options?.label;
    this.playbackRate = options?.playbackRate ?? 1.0;
    this.onPlaybackStarted = options?.onPlaybackStarted;
    this.onPlaybackEnded = options?.onPlaybackEnded;
    this.vegaCharacterRef = options?.vegaCharacterRef;
    if (isVegaLipSyncEnabled() && options?.vegaCharacterRef) {
      this.vegaSession = new VegaLipSyncSession(options.vegaCharacterRef, options.label ?? "pcm", this.sourceSampleRate);
    }
    // ~0.2s before play; Vega waits ~0.32s so lookahead visemes exist at t=0.
    this.minBufferSamples = Math.round(this.sourceSampleRate * (this.vegaSession ? 0.32 : 0.2));
  }

  async start(): Promise<void> {
    if (this.isStarted || this.stopped) return;
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.ctx = new AudioCtx();
    this.minBufferSamples = Math.round((this.ctx.sampleRate || this.sourceSampleRate) * (this.vegaSession ? 0.32 : 0.2));
    this.gainNode = this.ctx.createGain();
    // iOS ambient session: a 0-input ScriptProcessor runs the clock but stays silent after HTMLAudio pauses.
    // Trade only — agents already play through HTMLAudio and must keep the desktop/Capacitor URL path.
    const driveWithElement = isCapacitorNative() && this.label === "trade";
    let mediaSource: MediaElementAudioSourceNode | null = null;
    if (driveWithElement) {
      this.sessionAudioUrl = silentWavObjectUrl();
      this.sessionAudio = new Audio(this.sessionAudioUrl);
      this.sessionAudio.loop = true;
      this.sessionAudio.preload = "auto";
      this.sessionAudio.setAttribute("playsinline", "true");
      this.sessionAudio.volume = 1;
      mediaSource = this.ctx.createMediaElementSource(this.sessionAudio);
    }
    this.processor = this.ctx.createScriptProcessor(8192, driveWithElement ? 1 : 0, 1);

    // Init volume/mute from audioService
    const initialMuted = audioService.getState().isMuted;
    const initialVolume = audioService.getVolume();
    this.setMuted(initialMuted);
    this.setVolume(initialVolume);

    // Subscribe to changes
    this.unsubscribeMute = audioService.subscribe((muted: boolean) => {
      this.setMuted(muted);
    });
    this.unsubscribeVolume = audioService.subscribeToVolume((vol: number) => {
      this.setVolume(vol);
    });

    this.processor.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);

      // Check if we have enough buffer to start playing
      if (!this.isPlaying) {
        const totalSamples = this.queue.reduce((sum, chunk) => sum + chunk.length, 0);
        // Capacitor Vega: same as welcome — do not start speakers until the first viseme exists.
        const vegaReady = !this.vegaSession || this.vegaSession.hasCues() || !isCapacitorNative();
        if (totalSamples >= this.minBufferSamples && vegaReady) {
          this.isPlaying = true;
          if (this.onPlaybackStarted && !this.playbackStartedFired) {
            this.playbackStartedFired = true;
            try {
              this.onPlaybackStarted();
            } catch (e) {
              console.warn("[PcmChunkPlayer] onPlaybackStarted callback error:", e);
            }
          }
        } else {
          // Not enough buffer yet: output silence
          output.fill(0);
          return;
        }
      }

      let offset = 0;
      while (offset < output.length) {
        if (this.queue.length === 0) {
          // no data: fill silence
          output.fill(0, offset);
          // Track when queue becomes empty to detect playback end (only after signalEndOfPcmStream — avoids gaps between SSE chunks)
          if (this.isPlaying && this.onPlaybackEnded && this.endOfPcmStreamSignaled) {
            const now = performance.now();
            if (this.queueEmptyStartTime === null) {
              this.queueEmptyStartTime = now;
            } else if (now - this.queueEmptyStartTime >= this.QUEUE_EMPTY_THRESHOLD_MS) {
              // Queue has been empty for threshold time, playback has ended
              this.isPlaying = false;
              this.queueEmptyStartTime = null;
              try {
                this.onPlaybackEnded();
              } catch (e) {
                console.warn("[PcmChunkPlayer] onPlaybackEnded callback error:", e);
              }
            }
          }
          break;
        }
        // Reset empty start time when we have data again
        if (this.queueEmptyStartTime !== null) {
          this.queueEmptyStartTime = null;
        }
        const chunk = this.queue[0];
        const remaining = output.length - offset;
        if (chunk.length <= remaining) {
          output.set(chunk, offset);
          offset += chunk.length;
          this.queue.shift();
        } else {
          // partial consume
          output.set(chunk.subarray(0, remaining), offset);
          this.queue[0] = chunk.subarray(remaining);
          offset += remaining;
        }
      }
      this.playedOutputSamples += offset;
      // Underrun: wall clock keeps running while PCM is silent — freeze Vega to content time.
      if (this.isPlaying && offset < output.length) {
        this.syncVegaClockToContent();
      }
    };

    // Connect graph. The silent element is the pull source; the processor replaces its samples with PCM.
    if (mediaSource) mediaSource.connect(this.processor);
    this.processor.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
    if (this.sessionAudio) {
      try {
        await this.sessionAudio.play();
        console.log(`[PcmChunkPlayer:trade] session element playing ctx=${this.ctx?.state}`);
      } catch (e) {
        console.warn(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] trade session audio play failed`, e);
      }
    }
    if (this.stopped) return;
    if (this.ctx.state !== "running") {
      try {
        await this.ctx.resume();
      } catch (e) {
        console.warn(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] AudioContext resume failed`, e);
      }
    }
    if (this.stopped) return;
    this.isStarted = true;
  }

  stop(): void {
    this.stopped = true;
    if (this.processor) {
      try {
        this.processor.disconnect();
      } catch {}
      this.processor.onaudioprocess = null;
      this.processor = null;
    }
    if (this.gainNode) {
      try {
        this.gainNode.disconnect();
      } catch {}
      this.gainNode = null;
    }
    if (this.sessionAudio) {
      try {
        this.sessionAudio.pause();
        this.sessionAudio.src = "";
      } catch {}
      this.sessionAudio = null;
    }
    if (this.sessionAudioUrl) {
      URL.revokeObjectURL(this.sessionAudioUrl);
      this.sessionAudioUrl = null;
    }
    if (this.ctx) {
      try {
        this.ctx.close();
      } catch {}
      this.ctx = null;
    }
    this.vegaSession?.reset();
    this.vegaSession = null;
    this.queue = [];
    this.playedOutputSamples = 0;
    this.isPlaying = false;
    this.isPaused = false;
    this.savedGainValue = 1.0;
    this.queueEmptyStartTime = null;
    this.endOfPcmStreamSignaled = false;
    if (this.unsubscribeMute) {
      this.unsubscribeMute();
      this.unsubscribeMute = undefined;
    }
    if (this.unsubscribeVolume) {
      this.unsubscribeVolume();
      this.unsubscribeVolume = undefined;
    }
    this.isStarted = false;
  }

  /**
   * Call when the HTTP/SSE stream has finished sending PCM (e.g. after `complete` event).
   * Until then, brief empty-queue gaps do not fire onPlaybackEnded (avoids killing lip sync mid-stream).
   */
  signalEndOfPcmStream(): void {
    this.vegaSession?.flush();
    this.endOfPcmStreamSignaled = true;
  }

  addChunkBase64(base64Data: string, mimeType?: string): void {
    if (!base64Data) {
      // eslint-disable-next-line no-console
      console.warn(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Received empty base64Data, skipping`);
      return;
    }
    if (!this.isStarted || !this.ctx) {
      // eslint-disable-next-line no-console
      console.warn(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Player not started or context missing, skipping chunk (len: ${base64Data.length})`);
      return;
    }
    try {
      // Strip whitespace and normalize URL-safe base64
      let sanitized = base64Data.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

      // Handle concatenated base64 strings (e.g. from backend cache) which might have '=' in the middle
      const base64Chunks = sanitized.includes("=")
        ? sanitized.match(/(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})/g)?.filter(Boolean) || [sanitized]
        : [sanitized];

      for (const chunk of base64Chunks) {
        let currentChunk = chunk;
        const padLen = currentChunk.length % 4;
        if (padLen > 0) currentChunk += "=".repeat(4 - padLen);

        let binary: string;
        try {
          binary = atob(currentChunk);
        } catch {
          // If still invalid, strip any character not in base64 alphabet (handles truncation/noise)
          const cleaned = currentChunk.replace(/[^A-Za-z0-9+/=]/g, "");
          const padLen2 = cleaned.length % 4;
          const padded = padLen2 === 0 ? cleaned : cleaned + "=".repeat(4 - padLen2);
          binary = atob(padded);
        }

        const buffer = new ArrayBuffer(binary.length);
        if (buffer.byteLength === 0) continue;
        const view = new Uint8Array(buffer);
        for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);

        let pcmData: Int16Array;
        // Check if chunk is WAV (has header) or raw PCM
        const isWav =
          mimeType?.includes("wav") ||
          (view.length >= 12 && String.fromCharCode(view[0], view[1], view[2], view[3]) === "RIFF" && String.fromCharCode(view[8], view[9], view[10], view[11]) === "WAVE");

        if (isWav) {
          const wavHeaderSize = 44;
          if (view.length < wavHeaderSize) {
            console.warn(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] WAV chunk too small (${view.length} < ${wavHeaderSize}), skipping`);
            continue;
          }
          const pcmBuffer = buffer.slice(wavHeaderSize);
          pcmData = new Int16Array(pcmBuffer);
        } else {
          pcmData = new Int16Array(buffer);
        }

        const float32 = new Float32Array(pcmData.length);
        for (let i = 0; i < pcmData.length; i++) {
          float32[i] = Math.max(-1, Math.min(1, pcmData[i] / 32768));
        }

        // Analyze at source rate when the chunk is enqueued (Vega lookahead).
        this.vegaSession?.push(float32);

        const targetRate = this.ctx.sampleRate || 48000;
        const effectiveSourceRate = this.sourceSampleRate * this.playbackRate;
        const resampled = this.resampleLinear(float32, effectiveSourceRate, targetRate);
        this.queue.push(resampled);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] ❌ Failed to decode base64 chunk | base64Len: ${base64Data.length} | mimeType: ${mimeType} | Error:`, e);
    }
  }

  // Try to resume the underlying AudioContext (for iOS Safari mic toggling issues)
  async resumeIfSuspended(): Promise<void> {
    if (!this.ctx) return;

    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
        // eslint-disable-next-line no-console
        console.log(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Resumed suspended AudioContext`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Failed to resume AudioContext`, e);
      }
    }
  }

  // Pause playback (duck audio when user speaks)
  pause(): void {
    if (!this.gainNode || this.isPaused) return;
    this.savedGainValue = this.gainNode.gain.value;
    this.gainNode.gain.value = 0;
    this.isPaused = true;
    // eslint-disable-next-line no-console
    console.log(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Playback paused (user speaking)`);
  }

  // Resume playback (restore audio when user stops speaking or after background return).
  // On iOS, the AudioContext may be suspended when the app returns from background;
  // we must resume it before restoring gain, otherwise the ScriptProcessorNode won't fire.
  resume(): void {
    if (!this.gainNode || !this.isPaused) return;
    const muted = audioService.getState().isMuted;
    this.gainNode.gain.value = muted ? 0 : this.savedGainValue;
    this.isPaused = false;

    // Resume AudioContext if iOS suspended it during background
    this.resumeIfSuspended();

    // eslint-disable-next-line no-console
    console.log(`[PcmChunkPlayer${this.label ? `:${this.label}` : ""}] Playback resumed`);
  }

  private syncVegaClockToContent(): void {
    const character = this.vegaCharacterRef?.current;
    if (!character?.setPlaybackStartTime || !this.ctx) return;
    const contentTime = this.playedOutputSamples / (this.ctx.sampleRate || 48000);
    character.setPlaybackStartTime(performance.now() / 1000 - contentTime);
  }

  private resampleLinear(input: Float32Array, srcRate: number, dstRate: number): Float32Array {
    if (srcRate === dstRate) return input;
    const ratio = dstRate / srcRate;
    const outLength = Math.round(input.length * ratio);
    const output = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const srcPos = i / ratio;
      const i0 = Math.floor(srcPos);
      const i1 = Math.min(i0 + 1, input.length - 1);
      const t = srcPos - i0;
      output[i] = input[i0] * (1 - t) + input[i1] * t;
    }
    return output;
  }

  private setMuted(muted: boolean): void {
    if (!this.gainNode || this.isPaused) return; // Don't change gain if paused
    this.gainNode.gain.value = muted ? 0 : audioService.getVolume();
    if (!muted) {
      this.savedGainValue = audioService.getVolume();
    }
  }

  private setVolume(volume: number): void {
    if (!this.gainNode || this.isPaused) return; // Don't change gain if paused
    const muted = audioService.getState().isMuted;
    this.gainNode.gain.value = muted ? 0 : volume;
    if (!muted) {
      this.savedGainValue = volume;
    }
  }
}

// 1s mono 8 kHz WAV. One non-zero sample so iOS does not drop the element as empty silence.
function silentWavObjectUrl(): string {
  const sampleRate = 8000;
  const samples = sampleRate;
  const dataSize = samples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  view.setInt16(44, 1, true);
  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}
